/**
 * Cookie consent helper — single source of truth for opt-in to non-essential
 * trackers (PostHog analytics, Brevo Conversations chat).
 *
 * Strictly-necessary cookies (auth session_token) are NOT gated here — they
 * are exempt from EU/UK consent rules.
 *
 * Categories:
 *   - analytics : PostHog
 *   - chat      : Brevo Conversations widget
 *
 * Usage:
 *   import { getConsent, setConsent, hasConsent, acceptAll, rejectAll,
 *            hasDecided, onConsentChange } from "@/lib/consent";
 *
 * Persistence: localStorage under `sideb_consent_v1`. Bump the version key
 * if the schema or category list ever changes — that forces a re-prompt.
 */

const STORAGE_KEY = "sideb_consent_v1";
const EVENT_NAME = "sideb:consent-change";

const DEFAULT = Object.freeze({
  analytics: false,
  chat: false,
  decided: false,
  ts: null,
});

function readRaw() {
  if (typeof window === "undefined") return { ...DEFAULT };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT };
    const parsed = JSON.parse(raw);
    return {
      analytics: !!parsed.analytics,
      chat: !!parsed.chat,
      decided: !!parsed.decided,
      ts: parsed.ts || null,
    };
  } catch {
    return { ...DEFAULT };
  }
}

export function getConsent() {
  return readRaw();
}

export function hasConsent(category) {
  return !!readRaw()[category];
}

export function hasDecided() {
  return !!readRaw().decided;
}

export function setConsent(prefs) {
  if (typeof window === "undefined") return;
  const merged = {
    analytics: !!prefs.analytics,
    chat: !!prefs.chat,
    decided: true,
    ts: new Date().toISOString(),
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: merged }));
}

export function acceptAll() {
  setConsent({ analytics: true, chat: true });
}

export function rejectAll() {
  setConsent({ analytics: false, chat: false });
}

/**
 * Subscribe to consent changes. Returns an unsubscribe function.
 *   const off = onConsentChange((prefs) => { ... });
 *   off();
 */
export function onConsentChange(handler) {
  if (typeof window === "undefined") return () => {};
  const wrapped = (e) => handler(e.detail);
  window.addEventListener(EVENT_NAME, wrapped);
  return () => window.removeEventListener(EVENT_NAME, wrapped);
}

/**
 * Imperatively open the cookie preferences modal from anywhere in the app
 * (e.g. from a Footer link). The CookieConsent component subscribes to this
 * custom event.
 */
export function openPreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("sideb:open-cookie-preferences"));
}
